/**
 * CommunitiesLandingPage — "Find your people." discovery page.
 */

import React, { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MessageCircle } from 'lucide-react';
import AppHeader from '@/components/home/AppHeader';
import { fadeUp, stagger } from '@/lib/motion';
import { useCommunities } from './context/CommunitiesContext';
import { useModalState } from './hooks';
import { COMMUNITIES } from '@/constants/testIds';
import CommunitiesHero from './CommunitiesHero';
import CategoryNav from './CategoryNav';
import CommunityCard from './CommunityCard';
import CommunityRail from './CommunityRail';
import CreateCommunityModal from './CreateCommunityModal';
import EmptyState from './EmptyState';
import NotificationsBell from './NotificationsBell';

function MyCommunityCard({ community }) {
  return (
    <Link
      to={`/app/communities/${community.id}`}
      className="card-paper flex items-center justify-between gap-3 p-4 transition-all duration-300 ease-soft hover:-translate-y-0.5 hover:shadow-soft"
    >
      <div className="min-w-0">
        <p className="truncate font-display text-[15px] font-semibold text-ink">{community.name}</p>
        <p className="mt-0.5 text-xs text-subtle">
          {community.unreadCount > 0 ? `${community.unreadCount} new discussion${community.unreadCount > 1 ? 's' : ''}` : 'All caught up'}
        </p>
      </div>
      {community.unreadCount > 0 && (
        <span className="grid h-6 min-w-6 shrink-0 place-items-center rounded-full bg-coral px-1.5 text-xs font-bold text-[#FFF7F0]">
          {community.unreadCount}
        </span>
      )}
    </Link>
  );
}

export default function CommunitiesLandingPage() {
  const { communities, myCommunities, trendingCommunities, popularCommunities, recentlyActiveCommunities, recommendedCommunities, createCommunity } = useCommunities();
  const [isCreateOpen, openCreate, closeCreate] = useModalState(false);
  const [searchValue, setSearchValue] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const navigate = useNavigate();

  const filtered = useMemo(() => {
    const q = searchValue.trim().toLowerCase();
    return communities.filter(c => {
      const matchesCategory = activeCategory === 'All' || c.category === activeCategory;
      const matchesSearch = !q || c.name.toLowerCase().includes(q) || c.tagline.toLowerCase().includes(q) || c.tags?.some(t => t.toLowerCase().includes(q));
      return matchesCategory && matchesSearch;
    });
  }, [communities, activeCategory, searchValue]);

  const handleCreated = (community) => navigate(`/app/communities/${community.id}`);

  return (
    <div className="relative min-h-screen bg-cream" data-testid={COMMUNITIES.landingPage}>
      <AppHeader />

      <main className="pb-28 md:pb-16">
        <CommunitiesHero searchValue={searchValue} onSearchChange={setSearchValue} onCreateClick={openCreate} />

        <CategoryNav activeCategory={activeCategory} onChange={setActiveCategory} trailing={<NotificationsBell />} />

        {myCommunities.length > 0 && (
          <section className="u-container mt-12">
            <p className="u-eyebrow mb-3"><span className="inline-block h-1.5 w-1.5 rounded-full bg-coral" /> Your world</p>
            <h2 className="font-display text-2xl font-semibold tracking-tight text-ink sm:text-3xl">My Communities</h2>
            <motion.div variants={stagger} initial="hidden" animate="show" className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {myCommunities.map((c, i) => (
                <motion.div key={c.id} variants={fadeUp} custom={i}><MyCommunityCard community={c} /></motion.div>
              ))}
            </motion.div>
          </section>
        )}

        {activeCategory === 'All' && !searchValue && (
          <>
            <CommunityRail eyebrow="Just for you" title="Communities You May Like" communities={recommendedCommunities} getReason={c => c.reason} />
            <CommunityRail eyebrow="Mid-conversation right now" title="Trending Communities" communities={trendingCommunities} />
            <CommunityRail eyebrow="The biggest tables in the room" title="Popular Communities" communities={popularCommunities} />
            <CommunityRail eyebrow="Fresh discussions" title="Recently Active" communities={recentlyActiveCommunities} />
          </>
        )}

        <section className="u-container mt-14 md:mt-16">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="u-eyebrow mb-3"><span className="inline-block h-1.5 w-1.5 rounded-full bg-coral" /> Browse</p>
              <h2 className="font-display text-2xl font-semibold tracking-tight text-ink sm:text-3xl">
                {activeCategory === 'All' ? 'All Communities' : activeCategory}
              </h2>
            </div>
            <span className="hidden shrink-0 items-center gap-1.5 rounded-full border border-line bg-card px-3.5 py-1.5 text-xs font-medium text-subtle sm:inline-flex">
              <MessageCircle className="h-3.5 w-3.5" /> {filtered.length} communities
            </span>
          </div>

          {filtered.length === 0 ? (
            <div className="mt-6">
              <EmptyState type="search" onCTAClick={() => { setSearchValue(''); setActiveCategory('All'); }} />
            </div>
          ) : (
            <motion.div variants={stagger} initial="hidden" animate="show" className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {filtered.map((c, i) => (
                <motion.div key={c.id} variants={fadeUp} custom={i % 12}><CommunityCard community={c} /></motion.div>
              ))}
            </motion.div>
          )}
        </section>
      </main>

      <CreateCommunityModal isOpen={isCreateOpen} onClose={closeCreate} onCreated={handleCreated} />
    </div>
  );
}
